/**
 * Мои входы в рабочие системы.
 *
 * ── Что решает эта панель ───────────────────────────────────────────────────
 *
 * Рабочие системы — CRM и её раздел для WhatsApp — выбрасывают из сессии по
 * своему усмотрению: по сроку, при смене адреса, при входе с другого
 * устройства. До этой панели каждый такой случай означал одно и то же:
 * сотрудник посреди дня упирается в форму входа, идёт искать свой пароль и до
 * тех пор не работает.
 *
 * Здесь пара сохраняется один раз, а дальше оболочка подставляет её сама —
 * каждый раз, когда система показала форму входа.
 *
 * ── Почему пара у каждого своя ──────────────────────────────────────────────
 *
 * Панель показывает только СВОИ пары и правит только их. Общей учётной записи
 * на отдел здесь нет намеренно: в чужой системе действия числятся за тем, чьим
 * входом вошли, и общий вход стёр бы разницу между сотрудниками ровно там, где
 * она важнее всего.
 *
 * Администратор видит, что пара заполнена, но не её значение: право управлять
 * разделом не даёт права входить в систему под сотрудником.
 */

import { useCallback, useEffect, useState } from "react";
import type { SectionCredentialDto, SectionDto } from "../lib/api.js";
import { useSession } from "../state/session.tsx";
import { desktop } from "../lib/desktop.js";
import { describeError } from "../lib/errors.js";
import { ADMIN_PANEL, ADMIN_PANEL_TITLE } from "./adminPanel.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BellIcon,
  BellOffIcon,
  CheckIcon,
  LockIcon,
  TrashIcon,
} from "./icons.tsx";
import { cn } from "@/lib/utils";

export function SectionLoginsPanel() {
  const { api } = useSession();

  const [sections, setSections] = useState<SectionDto[]>([]);
  const [saved, setSaved] = useState<SectionCredentialDto[]>([]);
  /**
   * Выключенные оповещения.
   *
   * Держим именно выключенные, а не включённые: запись в базе появляется
   * только когда сотрудник изменил умолчание, а умолчание — «включено».
   */
  const [muted, setMuted] = useState<Set<string>>(new Set());
  /** Состояние пробного оповещения. Живёт только на экране. */
  const [notifyProbe, setNotifyProbe] = useState<
    "idle" | "waiting" | "sent" | "unsupported"
  >("idle");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [list, credentials, notifications] = await Promise.all([
        api.sections(),
        api.sectionCredentials(),
        api.sectionNotifications(),
      ]);
      setSections(list.items);
      setSaved(credentials.items);
      setMuted(
        new Set(
          notifications.items
            .filter((item) => !item.enabled)
            .map((item) => item.sectionId),
        ),
      );
      setError(null);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Все системы, у которых есть адрес.
   *
   * Раньше здесь стоял фильтр по заданным администратором селекторам, и панель
   * почти всегда оказывалась пустой: чтобы она ожила, кто-то должен был сперва
   * сходить в инструменты разработчика за CSS-селекторами. Сотрудник видел
   * «пока нечего заполнять» и не имел ни малейшего понятия, что делать дальше.
   *
   * Теперь форму находит сама оболочка, и предварительной настройки не нужно.
   */
  const fillable = sections.filter((section) => Boolean(section.url));

  /*
   * Переключение оповещений.
   *
   * Список разрешённых адресов уходит в оболочку ЦЕЛИКОМ и сразу: иначе
   * настройка вступала бы в силу только после перезапуска приложения, а
   * человек, выключивший оповещения, продолжал бы их получать и решил бы, что
   * переключатель не работает.
   */
  const toggleNotifications = async (
    section: SectionDto,
    enabled: boolean,
  ): Promise<void> => {
    const next = new Set(muted);
    if (enabled) next.delete(section.id);
    else next.add(section.id);

    setMuted(next);

    try {
      await api.setSectionNotifications(section.id, enabled);
      desktop()?.setNotificationOrigins?.(
        sections
          .filter((item) => item.url && !next.has(item.id))
          .map((item) => item.url as string),
      );
    } catch (cause) {
      // Возвращаем переключатель обратно: показывать выключенным то, что
      // осталось включённым, — худший исход, чем сообщение об ошибке.
      setMuted(muted);
      setError(describeError(cause));
    }
  };

  if (loading) {
    return (
      <section className={ADMIN_PANEL}>
        <p className="text-sm text-muted-foreground">Загрузка…</p>
      </section>
    );
  }

  return (
    <section className={cn(ADMIN_PANEL, "flex flex-col gap-4")}>
      <div>
        <h2 className={ADMIN_PANEL_TITLE}>Мои рабочие системы</h2>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          Заполните здесь логин и пароль от CRM и WhatsApp — те же, что вводите
          на их страницах входа. Дальше, когда система выбросит вас из сессии,
          приложение войдёт заново само: форму входа оно находит на странице и
          заполняет за вас.
        </p>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Пароль хранится зашифрованным и виден только вам — администратор видит
          лишь то, что вход заполнен. Здесь же включаются и выключаются
          оповещения: настройка своя у каждого сотрудника.
        </p>

        {/*
          Проверка оповещений.

          Оповещение из рабочей системы проходит четыре звена: страница его
          отправляет, приложение разрешает, macOS пропускает, человек видит.
          Когда оповещений нет, по одному лишь их отсутствию нельзя понять, где
          обрыв. Эта кнопка проверяет два последних звена разом.
        */}
        {desktop()?.testNotification && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setNotifyProbe("waiting");
                void desktop()
                  ?.testNotification?.()
                  .then((ok) => setNotifyProbe(ok ? "sent" : "unsupported"))
                  .catch(() => setNotifyProbe("unsupported"));
              }}
              className="gap-1.5"
            >
              <BellIcon className="size-4" />
              Проверить оповещения
            </Button>

            {notifyProbe === "sent" && (
              <span className="text-[12px] leading-relaxed text-muted-foreground">
                Отправлено. Видите его — значит система оповещения пропускает,
                и если из рабочей системы ничего не приходит, дело в самом
                сайте. Не видите — разрешите оповещения приложению в настройках
                macOS.
              </span>
            )}
            {notifyProbe === "unsupported" && (
              <span className="text-[12px] text-destructive">
                Система не поддерживает оповещения.
              </span>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      {fillable.length === 0 ? (
        <p className="text-sm leading-relaxed text-muted-foreground">
          Рабочих систем пока нет. Они появятся здесь, когда администратор
          заведёт разделы с адресами и откроет вам к ним доступ.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {fillable.map((section) => (
            <SectionLoginRow
              key={section.id}
              section={section}
              credential={saved.find((c) => c.sectionId === section.id) ?? null}
              notifications={!muted.has(section.id)}
              onNotificationsChange={(enabled) =>
                void toggleNotifications(section, enabled)
              }
              onChanged={load}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function SectionLoginRow({
  section,
  credential,
  notifications,
  onNotificationsChange,
  onChanged,
}: {
  section: SectionDto;
  credential: SectionCredentialDto | null;
  notifications: boolean;
  onNotificationsChange: (enabled: boolean) => void;
  onChanged: () => Promise<void>;
}) {
  const { api } = useSession();

  const [login, setLogin] = useState(credential?.login ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Логин из сохранённой пары появляется после загрузки, уже при
  // смонтированной строке.
  useEffect(() => {
    setLogin(credential?.login ?? "");
  }, [credential?.login]);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.saveSectionCredential(section.id, { login, password });

      /*
       * Оболочка узнаёт о паре сразу, не дожидаясь повторного открытия раздела.
       *
       * Иначе сохранение выглядело бы бездействием: сотрудник заполнил пару,
       * вернулся в раздел с открытой формой входа — и она всё ещё ждёт ввода.
       */
      if (section.url) {
        desktop()?.setSectionLogin?.({
          url: section.url,
          login,
          password,
          loginSelector: section.loginSelector,
          passwordSelector: section.passwordSelector,
          submitSelector: section.submitSelector,
        });
      }

      // Пароль в состоянии не держим: он уже сохранён, а поле, которое
      // продолжает его показывать, переживёт и уход с экрана.
      setPassword("");
      setDone(true);
      await onChanged();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const forget = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteSectionCredential(section.id);
      if (section.url) desktop()?.forgetSectionLogin?.(section.url);
      setLogin("");
      setPassword("");
      setDone(false);
      await onChanged();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-2xl border border-border bg-card p-3">
      <div className="flex items-center gap-2">
        <span className="grid size-8 flex-none place-items-center rounded-xl bg-secondary text-brand">
          <LockIcon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-heading">
            {section.title}
          </p>
          <p className="truncate text-[12px] text-muted-foreground">
            {section.url}
          </p>
        </div>
        {credential && (
          <span className="flex flex-none items-center gap-1 text-[12px] text-muted-foreground">
            <CheckIcon className="size-3.5" />
            вход сохранён
          </span>
        )}

        {/*
          Оповещения этой системы.

          Кнопка, а не переключатель-галочка: состояние читается по значку и
          подписи сразу, без необходимости соображать, что означает положение
          ползунка.
        */}
        <button
          type="button"
          onClick={() => onNotificationsChange(!notifications)}
          aria-pressed={notifications}
          title={
            notifications
              ? "Оповещения включены — выключить"
              : "Оповещения выключены — включить"
          }
          className={cn(
            "flex flex-none items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors",
            notifications
              ? "border-brand/40 bg-secondary text-brand"
              : "border-border bg-card text-muted-foreground hover:bg-secondary",
          )}
        >
          {notifications ? (
            <BellIcon className="size-3.5" />
          ) : (
            <BellOffIcon className="size-3.5" />
          )}
          {notifications ? "Оповещения" : "Без оповещений"}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          placeholder="Логин"
          autoComplete="off"
          className="min-w-0 flex-1"
          aria-label={`Логин для «${section.title}»`}
        />
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          // Заполненная пара не показывает пароль: значения у нас нет и быть
          // не должно, пока его не запросят для подстановки.
          placeholder={credential ? "Новый пароль" : "Пароль"}
          autoComplete="new-password"
          className="min-w-0 flex-1"
          aria-label={`Пароль для «${section.title}»`}
        />

        <Button
          size="sm"
          disabled={busy || !login.trim() || !password}
          onClick={() => void save()}
          className="flex-none"
        >
          {credential ? "Обновить" : "Сохранить"}
        </Button>

        {credential && (
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={busy}
            onClick={() => void forget()}
            aria-label="Удалить сохранённый вход"
            title="Удалить сохранённый вход"
            className="flex-none text-muted-foreground hover:text-destructive"
          >
            <TrashIcon className="size-4" />
          </Button>
        )}
      </div>

      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      {done && !error && (
        <p className="mt-2 text-sm text-muted-foreground">
          Готово. Откройте раздел «{section.title}» — если система покажет форму
          входа, приложение заполнит её само. Если этого не произошло, скажите
          администратору: у этой системы вход нестандартный, и её поля нужно
          указать в разделе вручную.
        </p>
      )}
    </li>
  );
}
